/**
 * DefaultModelPicker — the ONE "Default model" control (2026-09-28).
 *
 * Used by the agent editor, the Agent-of-Agents settings and the Concierge
 * persona modal. Its rows MIRROR the "Models users can pick" checkbox list
 * in the agent editor, row for row: the pinned release's display name
 * ("Claude Sonnet 5"), the description line under it, the provider mark and
 * provider name on the trailing edge — same typography and spacing, so the
 * dropdown reads as the same list with the ticks taken off.
 *
 *   • the value is a model FAMILY ('sonnet'); the release it runs is the
 *     tenant's pin, which is what the row is named after — never chosen here;
 *   • ONLY the models users can pick are listed — the surface's allowed list
 *     ∩ the families the organization has enabled. No greyed rows, no
 *     "turned off" entries: a default must be a model people can reach;
 *   • "Not set" is the first row, with a short line saying what the surface
 *     falls back to (the first pickable family for an agent, the platform
 *     default for a meta-agent);
 *   • when the pickable set changes under a stored default (the editor
 *     unticks it), the dropdown follows live and the default is cleared with
 *     a short notice — never a silent reset, never a stale value saved.
 *
 * Data: the parent passes the SAME `families` its "Models users can pick"
 * section already holds (the agent editor's `availableModels`) — no second
 * catalog. A surface with no such section (the AoA / Concierge modals) leaves
 * it out and the picker reads the tenant's enabled families from the cached
 * `GET /ai/models/` — the same records, with their descriptions and providers.
 * A stored value may still be a release id (older rows, or a meta-agent slot
 * pinned by the platform admin): it is matched to its family by id, pinned
 * release or any release in the family — the same rule the chat picker uses.
 */

import { useEffect, useMemo, useState } from 'react';
import { Select } from 'antd';
import { useLanguage } from '@/hooks/useLanguage';
import { cachedGet } from '@/services/cachedGet';
import { isFamilyUsable } from '@/utils/modelPicker';
import { findCatalogEntry } from '@/utils/modelLabels';
import { ProviderIcon } from './ProviderIcon';
import './DefaultModelPicker.css';

export interface DefaultModelFamily {
  id: string;
  name: string;
  description?: string;
  provider?: string;
  provider_name?: string;
  resolved_model?: string;
  resolved_model_name?: string;
  version_ids?: string[];
  aliases?: string[];
  enabled?: boolean;
}

interface Props {
  /** Stored ref — a family key, or a release id from an older row. */
  value: string | null | undefined;
  /** Called with the FAMILY key, or null for "not set". */
  onChange: (family: string | null) => void;
  /** The surface's "Models users can pick": 'all' | [] (= all) | refs. */
  allowedModels?: 'all' | string[];
  /** The families the parent's "Models users can pick" section already has. */
  families?: DefaultModelFamily[];
  disabled?: boolean;
  /** What "not set" falls back to on this surface — the row's second line. */
  unsetHint: string;
  /** Help line under the control (surface-specific wording). */
  help?: string;
  testId?: string;
}

const NOT_SET = '';

function matches(f: DefaultModelFamily, ref: string): boolean {
  return f.id === ref
    || f.resolved_model === ref
    || (Array.isArray(f.aliases) && f.aliases.includes(ref))
    || (Array.isArray(f.version_ids) && f.version_ids.includes(ref));
}

/** The row, exactly as the checkbox list draws it (minus the checkbox). */
function ModelRow({ name, description, provider, providerName }: {
  name: string; description?: string; provider?: string | null; providerName?: string;
}) {
  return (
    <span className="dmp-row">
      <span className="dmp-row-content">
        <span className="dmp-row-name" dir="auto">{name}</span>
        {description && <span className="dmp-row-tagline" dir="auto">{description}</span>}
      </span>
      {(provider || providerName) && (
        <span className="dmp-row-provider">
          <ProviderIcon provider={provider} />
          {providerName && <span className="dmp-row-provider-name">{providerName}</span>}
        </span>
      )}
    </span>
  );
}

export function DefaultModelPicker({
  value,
  onChange,
  allowedModels = 'all',
  families: givenFamilies,
  disabled = false,
  unsetHint,
  help,
  testId = 'default-model-picker',
}: Props) {
  const { t } = useLanguage();
  const [fetched, setFetched] = useState<DefaultModelFamily[] | null>(null);
  const [cleared, setCleared] = useState<string | null>(null);
  const needsFetch = givenFamilies === undefined;

  useEffect(() => {
    if (!needsFetch) return;
    let cancelled = false;
    (async () => {
      try {
        // The tenant's enabled families — the same cached call the chat's
        // model picker makes, so this is not a second catalog.
        const data = await cachedGet<{ models?: DefaultModelFamily[] } | DefaultModelFamily[]>('/ai/models/', 5 * 60_000);
        if (!cancelled) setFetched(Array.isArray(data) ? data : (data.models || []));
      } catch {
        if (!cancelled) setFetched([]);
      }
    })();
    return () => { cancelled = true; };
  }, [needsFetch]);

  const families = givenFamilies ?? fetched;
  const loading = families === null;

  const allowedRefs = useMemo<string[] | null>(() => {
    if (allowedModels === 'all' || !Array.isArray(allowedModels) || allowedModels.length === 0) return null;
    return allowedModels;
  }, [allowedModels]);

  /** Exactly the models users can pick: enabled AND in the allowed list. */
  const pickable = useMemo(
    () => (families ?? []).filter(f =>
      isFamilyUsable(f) && (!allowedRefs || allowedRefs.some(ref => matches(f, ref)))),
    [families, allowedRefs],
  );

  const selected = value ? findCatalogEntry(pickable, value) : undefined;

  // The stored default fell out of the pickable set (unticked in the editor,
  // or switched off for the organization): clear it and say so.
  useEffect(() => {
    if (loading || !value || selected) return;
    const known = findCatalogEntry(families ?? [], value);
    setCleared(known ? (known.resolved_model_name || known.name) : value);
    onChange(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, value, selected]);

  useEffect(() => {
    if (selected) setCleared(null);
  }, [selected]);

  const options = [
    {
      value: NOT_SET,
      label: <ModelRow name={t('models.defaultModel.notSet')} description={unsetHint} />,
      'data-testid': `${testId}-option-not-set`,
    },
    ...pickable.map(f => ({
      value: f.id,
      label: (
        <ModelRow
          name={f.resolved_model_name || f.name}
          description={f.description}
          provider={f.provider}
          providerName={f.provider_name}
        />
      ),
      'data-testid': `${testId}-option-${f.id}`,
    })),
  ];

  return (
    <div className="dmp" data-testid={testId}>
      <Select
        className="dmp-select"
        popupClassName="dmp-dropdown"
        value={selected ? selected.id : NOT_SET}
        loading={loading}
        disabled={disabled || loading}
        onChange={(v: string) => onChange(v || null)}
        options={options}
        optionLabelProp="label"
        aria-label={t('models.defaultModel.label')}
        data-testid={`${testId}-select`}
        placeholder={loading ? t('models.defaultModel.loading') : undefined}
        listHeight={320}
      />
      {!loading && pickable.length === 0 && (
        <p className="dmp-note dmp-note--warn" data-testid={`${testId}-empty`}>
          {t('models.defaultModel.noPickable')}
        </p>
      )}
      {cleared && (
        <p className="dmp-note dmp-note--warn" data-testid={`${testId}-cleared`} dir="auto">
          {t('models.defaultModel.cleared', { name: cleared })}
        </p>
      )}
      {help && <p className="form-help-text dmp-help">{help}</p>}
    </div>
  );
}

export default DefaultModelPicker;
